import { css, Global } from '@emotion/react';
import CloseDialogIcon from '@icons/close-dialog.svg?react';
import { PrimaryButton as Button } from '@ui/buttons';
import { DialogModal } from '@ui/dialogModal';
import { Tooltip } from '@ui/tooltip';
import { generateKey } from '@utils';
import { Fragment, useState } from 'react';
import type { InsightChart } from 'types/schema';
import Styled from './Caption.styled';

interface Props {
  data: Pick<InsightChart, 'datasets' | 'timeframe_groups' | 'location_groups'>;
  shouldOpenDialog: boolean;
}

const Caption = ({ data, shouldOpenDialog }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const {
    ['datasets']: datasets,
    ['timeframe_groups']: timeframeGroups,
    ['location_groups']: locationGroups
  } = data;
  const dataset = datasets.map(({ title }) => title).join(', ');
  const timeframes = timeframeGroups.map(({ group }) => group).join(', ');

  const getLocationNames = (locations: { name?: string; id?: string }[]) =>
    locations.map(({ name }) => name).join(', ');

  const dialogContent = (
    <Styled.DialogContent>
      <p>This question was asked in the following countries</p>
      <Styled.DefinitionList>
        {locationGroups.map((locationGroup, index) => (
          <Fragment key={generateKey(`dialog-location-term-${index}`)}>
            <Styled.DefinitionTitle>
              {locationGroup.group}
            </Styled.DefinitionTitle>
            <Styled.DefinitionDetails>
              {getLocationNames(locationGroup.details.locations)}
            </Styled.DefinitionDetails>
          </Fragment>
        ))}
      </Styled.DefinitionList>
    </Styled.DialogContent>
  );

  const locations = locationGroups.map((locationGroup, index) => {
    const delimiter = index < locationGroups.length - 1 ? ',' : '.';

    return shouldOpenDialog ? (
      <div
        className="caption-item"
        key={generateKey(`mobile-location-${index}`)}>
        <Styled.Location onClick={() => setIsDialogOpen(true)}>
          {locationGroup.group}
        </Styled.Location>
        {delimiter}
      </div>
    ) : (
      <Tooltip
        className="caption-item"
        classesPopper="location-tooltip"
        key={generateKey(`desktop-location-${index}`)}
        content={
          <Fragment>
            <b>This includes: </b>
            {getLocationNames(locationGroup.details.locations)}
          </Fragment>
        }
        variant="white"
        enterDelay={200}
        leaveDelay={0}
        testId="insight-visualization-source-location">
        <div>
          <Styled.Location>{locationGroup.group}</Styled.Location>
          {delimiter}
        </div>
      </Tooltip>
    );
  });

  const styles = css`
    .location-tooltip {
      min-width: auto !important;
      max-width: 240px !important;
    }
  `;

  return (
    <Fragment>
      <Global styles={styles} />
      <Styled.CaptionItem>Data set: {dataset},</Styled.CaptionItem>
      <Styled.CaptionItem>Timeframe: {timeframes},</Styled.CaptionItem>
      <Styled.CaptionItem>Location: {locations}</Styled.CaptionItem>
      <DialogModal
        open={isDialogOpen}
        title="Locations"
        headerButton={
          <Styled.CloseButton>
            <CloseDialogIcon onClick={() => setIsDialogOpen(false)} />
          </Styled.CloseButton>
        }
        main={dialogContent}
        footerRight={
          <Styled.FooterButton>
            <Button className="button" onClick={() => setIsDialogOpen(false)}>
              Ok, got it
            </Button>
          </Styled.FooterButton>
        }
        onClose={() => setIsDialogOpen(false)}
      />
    </Fragment>
  );
};

export default Caption;
